import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-arc text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--arc-cyan-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-arc-bg disabled:pointer-events-none disabled:opacity-50 active:scale-95",
  {
    variants: {
      variant: {
        default: "bg-[var(--arc-cyan-primary)] text-white shadow-[0_1px_2px_rgba(0,0,0,0.28)] hover:bg-[var(--arc-cyan-hover)] hover:shadow-[0_8px_28px_rgba(56,189,248,0.22)]",
        secondary: "bg-arc-panel-soft text-arc-text-main hover:bg-arc-panel border border-arc-border hover:border-[var(--arc-cyan-primary)]/30 hover:shadow-md",
        outline: "border border-arc-border bg-transparent hover:bg-arc-panel-soft hover:border-[var(--arc-cyan-primary)]/50 hover:shadow-md",
        ghost: "hover:bg-arc-panel-soft hover:shadow-sm",
        glass:
          "border border-white/10 bg-white/[0.06] text-arc-text-main shadow-sm backdrop-blur-xl hover:border-[var(--arc-cyan-primary)]/35 hover:bg-white/[0.1] hover:shadow-[0_0_24px_rgba(110,193,255,0.12)]",
        gold: "bg-[var(--arc-gold-primary)] text-arc-bg hover:bg-[var(--arc-gold-hover)] font-semibold shadow-lg hover:shadow-xl hover:shadow-[var(--arc-gold-glow)]",
        mythic: "bg-[var(--arc-cyan-primary)] text-white shadow-[0_1px_2px_rgba(0,0,0,0.28)] hover:bg-[var(--arc-cyan-hover)] hover:shadow-[0_8px_28px_rgba(56,189,248,0.22)] font-semibold",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-arc px-3",
        lg: "h-11 rounded-arc px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

// eslint-disable-next-line react-refresh/only-export-components -- cva variant map used by callers
export { Button, buttonVariants }

